import { useEffect, useState } from 'react'
import { useToast } from '@entur/alert'
import { useApi } from '../api-clients/client-hooks'
import { patchEmployee, patchSensitiveEmployeeData } from '../api-clients/eba-clients'
import { FetchState } from '../api-clients/api-types'
import { useEmployee } from '../contexts/employee-context'

export enum PatchState {
  NOT_STARTED,
  FETCH_NORMAL,
  FETCH_NORMAL_FINISHED,
  FETCH_SENSITIVE,
  SUCCESS,
}

type Config = {
  useToast?: boolean
  onSuccess?: () => void
}
export const useEmployeePatch = (
  config?: Config
): [FetchState, (body: Record<string, unknown>, sensitiveBody?: Record<string, unknown>) => void] => {
  const { addToast } = useToast()
  const { employee, setEmployee, setSensitiveInfo } = useEmployee()
  const [sensitiveBody, setSensitiveBody] = useState<Record<string, unknown>>()
  const [patchState, setPatchState] = useState(PatchState.NOT_STARTED)

  const [patchResponse, doPatch] = useApi(patchEmployee, {
    successCallback: (data) => {
      setEmployee({ ...data, customerPreferences: employee.customerPreferences })
      setPatchState(PatchState.FETCH_NORMAL_FINISHED)
    },
  })
  const [sensitiveDataResponses, doPatchSensitiveData] = useApi(patchSensitiveEmployeeData, {
    successCallback: (data) => {
      setSensitiveInfo(data)
      setPatchState(PatchState.SUCCESS)
    },
  })
  const [status, setStatus] = useState(FetchState.NOT_STARTED)

  const patch = (body: Record<string, unknown>, newSensitiveBody?: Record<string, unknown>) => {
    setStatus(FetchState.LOADING)
    setPatchState(PatchState.NOT_STARTED)
    setSensitiveBody(newSensitiveBody)
    if (Object.keys(body).length > 0) {
      setPatchState(PatchState.FETCH_NORMAL)
      doPatch({ id: employee.id, body })
    } else if (newSensitiveBody) {
      setPatchState(PatchState.FETCH_NORMAL_FINISHED)
    }
  }

  useEffect(() => {
    const success =
      (!sensitiveBody && patchResponse.status === FetchState.SUCCESS) ||
      (patchState === PatchState.SUCCESS && sensitiveDataResponses.status === FetchState.SUCCESS)

    if (sensitiveBody && patchState === PatchState.FETCH_NORMAL_FINISHED) {
      doPatchSensitiveData({ id: employee.id, body: sensitiveBody })
      setPatchState(PatchState.FETCH_SENSITIVE)
    } else if (success && status !== FetchState.SUCCESS) {
      setStatus(FetchState.SUCCESS)
      if (config?.useToast) {
        addToast('Rettighetseier er oppdatert!')
      }
      if (config?.onSuccess) {
        config?.onSuccess()
      }
    } else if (patchResponse.status === FetchState.ERROR || sensitiveDataResponses.status === FetchState.ERROR) {
      setStatus(FetchState.ERROR)
    }
  }, [
    patchResponse,
    sensitiveDataResponses,
    addToast,
    setStatus,
    config,
    sensitiveBody,
    doPatchSensitiveData,
    employee.id,
    status,
    patchState,
  ])

  return [status, patch]
}
